import { useCallback } from 'react';
import { clearMultiSelection } from '@components/MainStage/clearMultiSelection';
import { getFocusOnState } from '@components/MainStage/focusOn.store';
import { getListSearchState } from '@components/MainStage/listSearch.store';
import { useProject } from '@context/Project';
import { useProjectFilters } from '@context/ProjectFilters/ProjectFiltersProvider';

/**
 * Single-press "clear everything" used by Esc (AC9). Clears the multi-selection,
 * the Focus On filter, the list search, and — only when a filter is actually
 * active — the basic filters and the scope (domains) filter, returning the
 * canvas to its default state.
 *
 * The selection / focus / search clears are self-guarded no-ops, so pressing
 * Esc with nothing active does nothing. The filter resets are gated on an
 * active filter to avoid pointless `filtersSubject` emissions (and the phantom
 * redraw work they trigger) on every stray Esc.
 */
const useClearAll = () => {
  const { handleClearAllFilters, selectedFilters } = useProjectFilters();
  const {
    handleDomainsChange,
    toolbox: { domains },
  } = useProject();

  return useCallback(() => {
    clearMultiSelection();
    getFocusOnState().clear();
    getListSearchState().reset();

    const hasActiveFilters = selectedFilters.length > 0 || domains.length > 0;

    if (hasActiveFilters) {
      handleClearAllFilters();
      handleDomainsChange([]);
    }
  }, [handleClearAllFilters, handleDomainsChange, selectedFilters, domains]);
};

export default useClearAll;
